<template>
  <div class="stats-container">
    <div class="stat-selector-container">
      <TabButton
        v-for="statType in statsOptions"
        :key="statType"
        :active="statType === selectedStat"
        :on_click="() => changeStat(statType)"
        :text="parseTranslation(statType)"
      />

      <div class="downloadButtons" v-if="selectedStat === STAT_TYPES.QUESTIONS">
        <Loader v-if="loading" :size="20" color="#0695fd" />

        <button v-else @click="exportPPT">
          <DownloadFile />

          <div class="hover">
            {{ parseTranslation(DOWNLOAD_ALL_CHARTS) }}
          </div>
        </button>

        <button @click="exportXLSX">
          <DownloadFile />

          <div class="hover">
            {{ parseTranslation(DOWNLOAD_ALL_TABLES) }}
          </div>
        </button>
      </div>

      <button
        class="reload-btn"
        :class="{ statQuestion: selectedStat === STAT_TYPES.QUESTIONS }"
        @click="get_current_status"
      >
        <ReloadIcon />
        <span>{{ parseTranslation(RELOAD) }}</span>
      </button>
    </div>

    <div class="stat-container">
      <component
        :is="getComponent(selectedStat)"
        id="current-stat"
        :survey_status="survey_status"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import ReloadIcon from '@/components/imgs/Reload.vue'
import DownloadFile from '@/components/imgs/DownloadFile'
import Loader from '@/components/loading/Loader-3'

import { STAT_TYPES, statsOptions } from './stats.constants'

import Performance from './Performance.vue'
import TabButton from '../TabButton.vue'
import Quota from './Quota.vue'
import Questions from './Questions'

import { exportChart } from './Questions/utils/exportAllCharts'
import { exportTable } from './Questions/utils/exportAllTables'

import Pptxgen from 'pptxgenjs'
import * as XLSX from 'xlsx/xlsx.mjs'

import * as translationIndex from './index.translations'

const { default: translations, ...translationKeys } = translationIndex

export default {
  name: 'SurveyStats',

  components: {
    ReloadIcon,
    DownloadFile,
    Loader,

    Performance,
    Quota,
    Questions,
    TabButton,
  },

  props: {
    survey_status: {
      required: true,
      type: Object,
    },

    get_current_status: {
      required: true,
      type: Function,
    },
  },

  data() {
    return {
      translations,
      ...translationKeys,
      STAT_TYPES,
      selectedStat: STAT_TYPES.PERFORMANCE,
      statsOptions,
      loading: false,
      loadingTimeout: null,
    }
  },

  computed: {
    ...mapGetters(['surveyStatusWithChartConfig']),
  },

  methods: {
    ...mapActions(['setSurveyStatusWithChartConfig']),

    exportPPT() {
      exportChart(Pptxgen, this.surveyStatusWithChartConfig.results)
    },

    exportXLSX() {
      exportTable(XLSX, this.surveyStatusWithChartConfig.results)
    },

    getComponent(type) {
      switch (type) {
        case STAT_TYPES.PERFORMANCE:
          return 'Performance'
        case STAT_TYPES.QUOTA:
          return 'Quota'
        case STAT_TYPES.QUESTIONS:
          return 'Questions'
        default:
          throw new Error(`${type} is not a valid Stat type`)
      }
    },

    changeStat(stat) {
      this.selectedStat = stat

      if (this.selectedStat === STAT_TYPES.QUESTIONS) {
        this.setLoader()

        return
      }

      clearTimeout(this.loadingTimeout)

      this.loading = false
    },

    setLoader() {
      this.loading = true

      const FIVE_SECONDS = 5000

      // The setTimeout is being used with five seconds to give word cloud graphics time to render. After the specified time, the user can download all the graphics.

      this.loadingTimeout = setTimeout(() => {
        this.loading = false
      }, FIVE_SECONDS)
    },
  },

  created() {
    this.setSurveyStatusWithChartConfig(this.survey_status)
  },
}
</script>

<style lang="scss" scoped>
.stat-selector-container {
  @include flexMiddle;

  @media (max-width: 600px) {
    flex-direction: column;
  }

  .downloadButtons {
    @include flexAlignCenter;
    margin-left: auto;

    @media (max-width: 600px) {
      margin: 0 auto 20px auto;
    }

    button {
      position: relative;
      @include tipography(16);
      @include flexAlignCenter;
      margin-left: auto;
      @include transparentButton;

      .light & {
        color: $main;
      }

      @media (max-width: 600px) {
        margin: 0 0 20px 0;
      }

      &:last-of-type {
        margin-left: 30px;
      }

      &:hover {
        .hover {
          display: block;
        }
      }

      .hover {
        display: none;
        position: absolute;
        min-width: 170px;
        width: 100%;
        top: 40px;
        left: calc(50% - 84px);
        border-radius: 3px;
        background-color: rgba(0, 0, 0, 0.6);
        box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.11);
        padding: 10px;
        text-align: center;
        @include tipography(15);
        line-height: 18px;

        &::after {
          content: '';
          position: absolute;
          bottom: 100%;
          left: calc(50% - 8px);
          border-width: 8px;
          border-style: solid;
          border-color: transparent transparent rgba(0, 0, 0, 0.6) transparent;
        }
      }
    }
  }

  .reload-btn {
    margin-left: auto;
    @include transparentButton;

    svg {
      margin-right: 8px;
    }

    span {
      @include tipography(16);

      .light & {
        color: $main;
      }
    }

    &.statQuestion {
      margin-left: 40px;
    }

    @media (max-width: 600px) {
      margin: 0 0 20px 0;

      &.statQuestion {
        margin-left: 0;
      }
    }
  }
}

.stat-container {
  margin-top: 30px;
}
</style>
